import { plural, t } from "@lingui/macro";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretDownIcon,
  CaretUpIcon,
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  LockSimpleIcon,
  LockSimpleOpenIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

/**
 * SectionBlock — a collapsible section card in the form editor (builder v2).
 *
 * Presentational. Hosts a section's form (`children`) under a calm header with
 * drag handle, title + item count, and per-section controls: move up / down,
 * visibility, lock (AI-tailoring guard — distinct from visibility), collapse,
 * and delete. The parent owns open/visible/locked state and ordering.
 */
export type SectionBlockProps = {
  title: string;
  /** Phosphor node, e.g. <BriefcaseIcon size={18} />. */
  icon: ReactNode;
  /** Item count shown in the header; omit for single-entry sections. */
  count?: number;
  children: ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  visible?: boolean;
  onToggleVisibility?: () => void;
  locked?: boolean;
  onToggleLock?: () => void;
  onDelete?: () => void;
  /** Reorder the whole section within the list. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /** Spread onto the drag handle by a dnd-kit listener in the parent. */
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  /** Optional tip shown when expanded. */
  tip?: string;
};

export const SectionBlock = ({
  title,
  icon,
  count,
  children,
  collapsed = false,
  onToggleCollapse,
  visible = true,
  onToggleVisibility,
  locked = false,
  onToggleLock,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  dragHandleProps,
  tip,
}: SectionBlockProps) => {
  const countLabel =
    count === undefined ? null : plural(count, { one: "# item", other: "# items" });

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border bg-background transition-colors",
        collapsed ? "border-border" : "border-primary/30 shadow-sm",
        !visible && "opacity-70",
      )}
    >
      <div className="flex items-center gap-2 p-3">
        {dragHandleProps ? (
          <button
            type="button"
            aria-label={t`Drag to reorder`}
            className="flex h-7 w-5 cursor-grab items-center justify-center text-zinc-400 hover:text-foreground active:cursor-grabbing"
            {...dragHandleProps}
          >
            <DotsSixVerticalIcon size={16} />
          </button>
        ) : null}

        <button
          type="button"
          className="flex flex-1 items-center gap-3 text-left"
          aria-expanded={!collapsed}
          onClick={onToggleCollapse}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">{title}</span>
            <span className="block font-mono text-[10px] text-muted-foreground">
              {countLabel}
              {countLabel && visible ? "" : (countLabel ? " · " : "")}
              {visible ? "" : t`hidden`}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-0.5">
          {onMoveUp ? (
            <Tooltip content={t`Move up`}>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground"
                disabled={!canMoveUp}
                aria-label={t`Move section up`}
                onClick={onMoveUp}
              >
                <ArrowUpIcon size={15} />
              </Button>
            </Tooltip>
          ) : null}

          {onMoveDown ? (
            <Tooltip content={t`Move down`}>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground"
                disabled={!canMoveDown}
                aria-label={t`Move section down`}
                onClick={onMoveDown}
              >
                <ArrowDownIcon size={15} />
              </Button>
            </Tooltip>
          ) : null}

          {onToggleLock ? (
            <Tooltip content={locked ? t`Unlock from tailoring` : t`Lock from tailoring`}>
              <Button
                size="icon"
                variant="ghost"
                className={cn("h-8 w-8", locked && "text-primary")}
                role="switch"
                aria-checked={locked}
                aria-label={t`Lock from AI tailoring`}
                onClick={onToggleLock}
              >
                {locked ? <LockSimpleIcon size={15} /> : <LockSimpleOpenIcon size={15} />}
              </Button>
            </Tooltip>
          ) : null}

          {onToggleVisibility ? (
            <Tooltip content={visible ? t`Hide section` : t`Show section`}>
              <Button
                size="icon"
                variant="ghost"
                className={cn("h-8 w-8", visible ? "text-primary" : "text-muted-foreground")}
                role="switch"
                aria-checked={visible}
                aria-label={t`Section visibility`}
                onClick={onToggleVisibility}
              >
                {visible ? <EyeIcon size={15} /> : <EyeSlashIcon size={15} />}
              </Button>
            </Tooltip>
          ) : null}

          {onDelete ? (
            <Tooltip content={t`Delete section`}>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-rose-500"
                aria-label={t`Delete section`}
                onClick={onDelete}
              >
                <TrashIcon size={15} />
              </Button>
            </Tooltip>
          ) : null}

          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label={collapsed ? t`Expand section` : t`Collapse section`}
            onClick={onToggleCollapse}
          >
            {collapsed ? <CaretDownIcon size={15} /> : <CaretUpIcon size={15} />}
          </Button>
        </div>
      </div>

      {collapsed ? null : (
        <div className="border-t border-border p-4">
          {tip ? (
            <p className="mb-4 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs leading-relaxed text-primary">
              {tip}
            </p>
          ) : null}
          {children}
        </div>
      )}
    </section>
  );
};
